package com.iregained.site.components.sections.about.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.about.styles.*
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Bento Cascade
@Composable
private fun Modifier.bentoCascade(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else 30.px)
        .scale(if (isVisible) 1f else 0.9f).transition(
            Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
            Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms)
        )
}

@Composable
fun AboutValuesSection(data: AboutValuesSectionData = defaultAboutValuesData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "about-values-section"
    var isVisible by remember { mutableStateOf(false) }

    if (data.cards.isNotEmpty()) {
        Column(
            modifier = AboutValuesContainerStyle.toModifier().id(targetId).fillMaxWidth()
                .backgroundColor(palette.brand.primarySoft)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                    .justifyContent(JustifyContent.Center).toAttrs {
                    ref { element ->
                        val observer = IntersectionObserver { entries, obs ->
                            entries.forEach {
                                if (it.isIntersecting) {
                                    isVisible = true; obs.unobserve(element)
                                }
                            }
                        }
                        observer.observe(element); onDispose { observer.disconnect() }
                    }
                }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.sectionTitle.isNotBlank()) {
                        SectionHeader(
                            text = data.sectionTitle,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 16.px).opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s)),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    if (data.sectionSubtitle.isNotBlank()) {
                        Div(
                            attrs = SubheadlineTextStyle.toModifier().maxWidth(1200.px).fontSize(18.px)
                                .fontWeight(FontWeight.Medium).color(palette.custom.darkText)
                                .textAlign(TextAlign.Center).margin(bottom = 40.px).opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s, delay = 150.ms)).toAttrs {
                                ref { htmlElement ->
                                    htmlElement.innerHTML = data.sectionSubtitle; onDispose {}
                                }
                            })
                    }

                    SimpleGrid(numColumns(base = 1, lg = 3), modifier = AboutValuesGridStyle.toModifier()) {
                        data.cards.forEachIndexed { index, card ->
                            key(card.title) {
                                Box(
                                    modifier = AboutValueCardStyle.toModifier()
                                        .bentoCascade(isVisible, 300 + (index * 150))
                                ) {
                                    Column {
                                        if (card.iconPath.isNotBlank()) Image(
                                            src = card.iconPath,
                                            alt = card.title,
                                            modifier = AboutValueIconStyle.toModifier()
                                        )
                                        if (card.title.isNotBlank()) SpanText(
                                            text = card.title,
                                            modifier = AboutValueCardTitleStyle.toModifier()
                                        )
                                        if (card.description.isNotBlank()) Div(
                                            attrs = AboutValueCardDescStyle.toModifier().toAttrs {
                                                ref { htmlElement ->
                                                    htmlElement.innerHTML = card.description; onDispose {}
                                                }
                                            })
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL & MEDIA SPECS
// ==========================================
/**
 * CMS IMAGE UPLOAD SPECS FOR VALUES GRID:
 * @field iconPath:
 * - Aspect Ratio: 1:1 (Square).
 * - Recommended Dimensions: 64x64px to 128x128px.
 * - Format: SVG (Highly Preferred) to ensure crisp scaling on retina displays.
 * - Content: Monochromatic icons matching your brand Teal. Minimalist line-art.
 */
data class ValueCardData(
    val iconPath: String, val title: String, val description: String
)

data class AboutValuesSectionData(
    val sectionId: String? = null,
    val sectionTitle: String = "Core Pillars of Our Solution",
    val sectionSubtitle: String = "We believe true rehabilitation requires a seamless integration of clinical science, physical engineering, and digital engagement.",
    val cards: List<ValueCardData> = listOf(
        ValueCardData(
            iconPath = Res.Images.About.Values.HARDWARE,
            title = "Medical-Grade Hardware",
            description = "Engineered for high-volume clinical environments. Our mechatronic system features wipeable surfaces, robust fail-safes, and dynamic force adaptation to ensure safe, repeatable therapy."
        ), ValueCardData(
            iconPath = Res.Images.About.Values.SOFTWARE,
            title = "Intelligent DTx Software",
            description = "Multi-modal therapy options with pre-programmed protocols, custom protocol & games. Our DTx Platform not just tracks every movement accurately for bio-feedback, but also translates them into actionable clinical data for personalized and effective rehabilitation."
        ), ValueCardData(
            iconPath = Res.Images.About.Values.PATIENT,
            title = "Personalized Therapy",
            description = "Neuroplasticity requires thousands of repetitions. We mask grueling physical therapy inside immersive, task-specific gameplay that keeps stroke survivors highly motivated."
        )
    )
)

val defaultAboutValuesData = AboutValuesSectionData()